import React, { useCallback, useMemo, useRef, useState } from 'react'

import ActionButtonsSelector from 'v2/views/ActionButtonsSelector'

import { ActionsContextProvider } from 'features/actions/ActionsContextProvider'
import { FieldEditorPopover } from 'features/admin/fields/FieldEditorPopover'
import { getFieldIcon } from 'features/admin/fields/icons/utils'
import { toYType } from 'features/utils/useYjsState'
import * as yUtils from 'features/utils/yUtils'
import {
    DetailViewTabControl,
    DetailViewTabControlItem,
} from 'features/views/DetailView/DetailViewTabControl'
import { LayoutEditorControlSeparator } from 'features/views/LayoutEditor/controls/LayoutEditorControlSeparator'
import { LayoutEditorVisibilityFiltersControl } from 'features/views/LayoutEditor/controls/LayoutEditorVisibilityFiltersControl'
import { WidgetAdminControlsComponent } from 'features/views/LayoutEditor/types'
import { useLayoutEditorContext } from 'features/views/LayoutEditor/useLayoutEditorContext'
import { FieldCollectionDetailsControl } from 'features/views/LayoutEditor/widgets/common/FieldCollection/FieldCollectionDetailControl'
import { FieldCollectionAttribute } from 'features/views/LayoutEditor/widgets/common/FieldCollection/types'

import FieldListEditor from 'v2/ui/components/FieldsEditor/FieldListEditor'
import { Item } from 'v2/ui/components/OrderableListSelector/types'
import useDebounce from 'v2/ui/utils/useDebounce'
import useEffectOnlyOnUpdate from 'v2/ui/utils/useEffectOnlyOnUpdate'

import { Box } from 'ui/components/Box'
import { Button } from 'ui/components/Button'
import { Field, LabelText } from 'ui/components/Field'
import { Input } from 'ui/components/Input'
import { RadioButton, RadioCard, RadioCardGroup, RadioGroup } from 'ui/components/Radio'
import { Select, SelectOption } from 'ui/components/Select'
import { Body } from 'ui/components/Text'
import { Toggle } from 'ui/components/Toggle'
import { Tooltip } from 'ui/components/Tooltip'
import { theme } from 'ui/styling/Theme.css'

import { useRelatedRecordWidgetTarget } from './hooks/useRelatedRecordWidgetTarget'
import {
    RelatedRecordWidgetField,
    RelatedRecordWidgetFieldItem,
    RelatedRecordWidgetFieldRef,
    RelatedRecordWidgetSelfRef,
    RelatedRecordWidgetType,
} from './types'
import {
    getFieldRef,
    getSupportedDescriptionFields,
    getSupportedProfileImageFields,
    getSupportedSubtitleFields,
    getSupportedTargetFields,
    getSupportedTitleFields,
} from './utils'

type RelatedRecordWidgetAdminControlsProps = {}

export const RelatedRecordWidgetAdminControls: WidgetAdminControlsComponent<
    RelatedRecordWidgetType,
    RelatedRecordWidgetAdminControlsProps
> = ({ widget, onChange, ...props }) => {
    const { targetObjectFields, targetObject, displayedFields } =
        useRelatedRecordWidgetTarget(widget)

    const canCustomizeControls = !!targetObject

    return (
        <DetailViewTabControl>
            <DetailViewTabControlItem value="content" label="Content">
                <RelatedRecordWidgetContentControls
                    widget={widget}
                    onChange={onChange}
                    object={targetObject}
                    fields={targetObjectFields}
                    displayedFields={displayedFields}
                    {...props}
                />
            </DetailViewTabControlItem>
            <DetailViewTabControlItem value="style" label="Style" disabled={!canCustomizeControls}>
                <RelatedRecordWidgetStyleControls widget={widget} onChange={onChange} {...props} />
            </DetailViewTabControlItem>
            <DetailViewTabControlItem
                value="controls"
                label="Controls"
                disabled={!canCustomizeControls}
            >
                <RelatedRecordWidgetActionControls
                    widget={widget}
                    onChange={onChange}
                    object={targetObject}
                    {...props}
                />
            </DetailViewTabControlItem>
        </DetailViewTabControl>
    )
}

const DEBOUNCE_RATE = 300 // ms
const LABEL_MAX_LENGTH = 20

type RelatedRecordWidgetContentControlsProps = {
    object?: ObjectDto
    fields?: FieldDto[]
    displayedFields?: RelatedRecordWidgetFieldItem[]
}

const RelatedRecordWidgetContentControls: WidgetAdminControlsComponent<
    RelatedRecordWidgetType,
    RelatedRecordWidgetContentControlsProps
> = ({
    widget,
    onChange,
    object: targetObject,
    fields: targetFields = [],
    displayedFields = [],
    ...props
}) => {
    const { fields: localFields } = useLayoutEditorContext()

    const { targetField, profileImage, label = '', title, subtitle, description } = widget.attrs

    const targetFieldsByApiName = new Map(targetFields.map((field) => [field.api_name, field]))

    const supportedTargetFields = getSupportedTargetFields(localFields)
    const onChangeTargetField = (value: string) => {
        const field = localFields?.find((f) => f.api_name === value)
        onChange((data) => data.set('targetField', getFieldRef(field)))
    }

    const targetFieldType = targetField?.type ?? 'field'
    const onChangeTargetFieldType = (value: string) => {
        const newVal: RelatedRecordWidgetFieldRef | RelatedRecordWidgetSelfRef =
            value === 'field'
                ? {
                      type: 'field',
                      fieldApiName: '',
                      fieldSid: '',
                  }
                : {
                      type: 'self',
                  }

        onChange((data) => data.set('targetField', newVal))
    }

    // Reset the fields when the target object changes.
    const targetObjectSid = targetObject?._sid
    const prevTargetObjectSid = useRef(targetObjectSid)
    useEffectOnlyOnUpdate(() => {
        if (targetObjectSid === prevTargetObjectSid.current) return

        onChange((attrs) => {
            attrs.set('fields', toYType([]))
            attrs.set('showAllFields', false)
        })

        prevTargetObjectSid.current = targetObjectSid
    }, [targetObjectSid, onChange])

    const profileImageField = targetFieldsByApiName.get(profileImage?.fieldApiName!)
    const supportedProfileImageFields = getSupportedProfileImageFields(targetFields)
    const onChangeProfileImageField = (value?: string) => {
        const field = targetFields?.find((f) => f.api_name === value)
        onChange((attrs) => attrs.set('profileImage', getFieldRef(field)))
    }

    const titleField = targetFieldsByApiName.get(title?.fieldApiName!)
    const supportedTitleFields = getSupportedTitleFields(targetFields)
    const onChangeTitleField = (value?: string) => {
        const field = targetFields?.find((f) => f.api_name === value)
        onChange((attrs) => attrs.set('title', getFieldRef(field)))
    }

    const subtitleField = targetFieldsByApiName.get(subtitle?.fieldApiName!)
    const supportedSubtitleFields = getSupportedSubtitleFields(targetFields)
    const onChangeSubtitleField = (value?: string) => {
        const field = targetFields?.find((f) => f.api_name === value)
        onChange((attrs) => attrs.set('subtitle', getFieldRef(field)))
    }

    const descriptionField = targetFieldsByApiName.get(description?.fieldApiName!)
    const supportedDescriptionFields = getSupportedDescriptionFields(targetFields)
    const onChangeDescriptionField = (value?: string) => {
        const field = targetFields?.find((f) => f.api_name === value)
        onChange((attrs) => attrs.set('description', getFieldRef(field)))
    }

    const debouncedOnChange = useDebounce(onChange, DEBOUNCE_RATE) as typeof onChange

    const [localLabel, setLocalLabel] = useState(label)
    const onChangeLabel = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value

            setLocalLabel(value)
            debouncedOnChange((attrs) => {
                attrs.set('label', value)
            })
        },
        [debouncedOnChange]
    )

    return (
        <Box flex column height="full" gap="l">
            <Box px="l" flex column gap="l">
                <Field label="Data source">
                    <RadioCardGroup
                        value={targetFieldType}
                        onValueChange={onChangeTargetFieldType}
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, 1fr)',
                        }}
                    >
                        <RadioCard value="field" icon={{ name: 'Text' }}>
                            From field
                        </RadioCard>
                        <RadioCard value="self" icon={{ name: 'RotateCcw' }}>
                            Current record
                        </RadioCard>
                    </RadioCardGroup>
                </Field>
                {targetFieldType === 'field' && (
                    <Select
                        placeholder="Select field..."
                        label="Related record field"
                        isSearchable
                        value={(targetField as RelatedRecordWidgetFieldRef)?.fieldApiName ?? ''}
                        onChange={onChangeTargetField}
                    >
                        {supportedTargetFields.map((field) => (
                            <SelectOption
                                key={field.api_name}
                                value={field.api_name}
                                label={field.label}
                                startIcon={getFieldIcon(field)}
                            />
                        ))}
                    </Select>
                )}
                {targetObject && (
                    <>
                        <Select
                            placeholder="Select field..."
                            label="Profile image"
                            isSearchable
                            isClearable
                            value={profileImageField?.api_name}
                            onChange={onChangeProfileImageField}
                        >
                            {supportedProfileImageFields.map((field) => (
                                <SelectOption
                                    key={field.api_name}
                                    value={field.api_name}
                                    label={field.label}
                                    startIcon={getFieldIcon(field)}
                                />
                            ))}
                        </Select>
                        <Input
                            label="Label"
                            value={localLabel}
                            onChange={onChangeLabel}
                            maxLength={LABEL_MAX_LENGTH}
                        />
                        <Select
                            placeholder="Select field..."
                            label="Title"
                            isSearchable
                            isClearable
                            value={titleField?.api_name}
                            onChange={onChangeTitleField}
                        >
                            {supportedTitleFields.map((field) => (
                                <SelectOption
                                    key={field.api_name}
                                    value={field.api_name}
                                    label={field.label}
                                    startIcon={getFieldIcon(field)}
                                />
                            ))}
                        </Select>
                        <Select
                            placeholder="Select field..."
                            label="Subtitle"
                            isSearchable
                            isClearable
                            value={subtitleField?.api_name}
                            onChange={onChangeSubtitleField}
                        >
                            {supportedSubtitleFields.map((field) => (
                                <SelectOption
                                    key={field.api_name}
                                    value={field.api_name}
                                    label={field.label}
                                    startIcon={getFieldIcon(field)}
                                />
                            ))}
                        </Select>
                        <Select
                            placeholder="Select field..."
                            label="Description"
                            isSearchable
                            isClearable
                            value={descriptionField?.api_name}
                            onChange={onChangeDescriptionField}
                        >
                            {supportedDescriptionFields.map((field) => (
                                <SelectOption
                                    key={field.api_name}
                                    value={field.api_name}
                                    label={field.label}
                                    startIcon={getFieldIcon(field)}
                                />
                            ))}
                        </Select>
                        <RelatedRecordWidgetFieldsControl
                            widget={widget}
                            onChange={onChange}
                            object={targetObject}
                            fields={targetFields}
                            displayedFields={displayedFields}
                            {...props}
                        />
                    </>
                )}
            </Box>
            <LayoutEditorControlSeparator />
            <LayoutEditorVisibilityFiltersControl widget={widget} onChange={onChange} />
        </Box>
    )
}

type FieldItem = Item & {
    fieldId: string
    fieldName: string
    conditions: Filter[]
    fieldLabel: string
    originalField?: RelatedRecordWidgetFieldItem
}

type RelatedRecordWidgetFieldsControlProps = {
    object: ObjectDto
    displayedFields: RelatedRecordWidgetFieldItem[]
    fields?: FieldDto[]
}

const RelatedRecordWidgetFieldsControl: WidgetAdminControlsComponent<
    RelatedRecordWidgetType,
    RelatedRecordWidgetFieldsControlProps
> = ({ widget, onChange, object, fields, displayedFields, openDetailPane }) => {
    const { showAllFields } = widget.attrs

    const addFieldTargetRef = useRef<HTMLButtonElement>(null)

    const [isAddFieldOpen, setIsAddFieldOpen] = useState(false)

    const onAddFieldClick = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            setIsAddFieldOpen((prev) => !prev)
            e.preventDefault()
            e.stopPropagation()
        },
        [setIsAddFieldOpen]
    )

    const onAddFieldClose = useCallback(() => {
        setIsAddFieldOpen(false)
    }, [setIsAddFieldOpen])

    const onFieldUpdate = useCallback(
        (items: FieldItem[]) => {
            onChange((attrs) => {
                const newFields: RelatedRecordWidgetField[] = items.map((i) => {
                    if (!i.originalField) {
                        return {
                            sid: i.fieldId,
                            apiName: i.fieldName,
                            conditions: i.conditions ?? [],
                        }
                    }

                    return {
                        ...i.originalField,
                    }
                })

                attrs.set('fields', toYType(newFields))
            })
        },
        [onChange]
    )

    const onFieldEdit = useCallback(
        (item: FieldItem) => {
            openDetailPane({
                component: (props) => <FieldDetailsControl {...props} fieldSid={item.fieldId} />,
                label: `Field: ${item.fieldLabel}`,
            })
        },
        [openDetailPane]
    )

    const conditionalVisibilityFilters = useMemo(() => {
        return displayedFields.reduce(
            (acc, f) => {
                acc[f.sid] = f.conditions

                return acc
            },
            {} as Record<string, Filter[]>
        )
    }, [displayedFields])

    const onChangeConditionalVisibilityFilters = useCallback(
        (filters: Record<string, Filter[]>) => {
            onChange((attrs) => {
                const fields = attrs.get('fields')

                for (const field of displayedFields) {
                    const changedFilters = filters[field.sid]
                    if (!changedFilters) continue

                    const existingField = yUtils.arrayFind<typeof attrs>(
                        fields,
                        (f) => f.get('sid') === field.sid
                    )
                    if (existingField) {
                        existingField.set('conditions', toYType(changedFilters))
                    } else if (changedFilters.length > 0) {
                        fields.push([
                            toYType({
                                sid: field.sid,
                                apiName: field.apiName,
                                conditions: changedFilters,
                            } as RelatedRecordWidgetField),
                        ])
                    }
                }
            })
        },
        [onChange, displayedFields]
    )

    const onChangeShowAllFields = useCallback(
        (value: string) => {
            onChange((attrs) => {
                attrs.set('showAllFields', value === 'showAllFields')
            })
        },
        [onChange]
    )

    if (!fields) return null

    return (
        <>
            <Box>
                <LabelText>Fields</LabelText>
                <Box flex alignItems="baseline" justifyContent="space-between" mt="2xs">
                    <Box role="group" column flex gap="m">
                        <RadioGroup
                            value={showAllFields ? 'showAllFields' : 'specific'}
                            onValueChange={onChangeShowAllFields}
                            style={{
                                gap: theme.space.xs,
                            }}
                        >
                            <RadioButton value="showAllFields">
                                <Body size="s">Show all fields</Body>
                            </RadioButton>
                            <RadioButton value="specific">
                                <Body size="s">Choose fields to display</Body>
                            </RadioButton>
                        </RadioGroup>
                    </Box>
                    <Button
                        variant="primary"
                        size="xs"
                        startIcon={{ name: 'Plus' }}
                        ref={addFieldTargetRef}
                        onClick={onAddFieldClick}
                    >
                        Add new
                    </Button>
                </Box>
            </Box>
            <Box>
                <FieldListEditor
                    object={object}
                    fields={fields}
                    selectedItems={displayedFields.map((f) => ({
                        fieldId: f.field._sid,
                        fieldName: f.field.api_name,
                        fieldLabel: f.label || f.field.label,
                        label: f.label || f.field.label,
                        objectId: f.field.object_id,
                        readOnly: false,
                        required: false,
                        conditions: f.conditions,
                        originalField: f,
                    }))}
                    allowEditFields={true}
                    onUpdate={onFieldUpdate}
                    onEdit={onFieldEdit as any}
                    allFieldsModeEnabled={showAllFields}
                    disallowSections={true}
                    autoHideEditButton={true}
                    allowEditSections={false}
                    hideTopSection={true}
                    showConditionalVisibility={true}
                    conditionalVisibilityFilters={conditionalVisibilityFilters}
                    onConditionalVisibilityFiltersChange={onChangeConditionalVisibilityFilters}
                />
            </Box>
            <FieldEditorPopover
                objectId={object._sid}
                open={isAddFieldOpen}
                target={addFieldTargetRef.current ?? undefined}
                onSuccess={onAddFieldClose}
                onCancel={onAddFieldClose}
                onClose={onAddFieldClose}
                placement="bottom-start"
            />
        </>
    )
}

type RelatedRecordWidgetStyleControlsProps = {}

const RelatedRecordWidgetStyleControls: WidgetAdminControlsComponent<
    RelatedRecordWidgetType,
    RelatedRecordWidgetStyleControlsProps
> = ({ widget, onChange }) => {
    const showFieldIcon = widget.attrs.showFieldIcon ?? true
    const onChangeShowFieldIcon = useCallback(
        (value: boolean) => {
            onChange((attrs) => attrs.set('showFieldIcon', value))
        },
        [onChange]
    )

    const alignment = widget.attrs.alignment ?? 'left'
    const onChangeAlignment = useCallback(
        (value: string) => {
            onChange((attrs) => attrs.set('alignment', value))
        },
        [onChange]
    )

    const labelPlacement = widget.attrs.labelPlacement ?? 'top'
    const onChangeLabelPlacement = useCallback(
        (value: string) => {
            onChange((attrs) => attrs.set('labelPlacement', value))
        },
        [onChange]
    )

    return (
        <Box px="l" flex column gap="3xl">
            <Field label="Alignment">
                <RadioCardGroup
                    value={alignment}
                    onValueChange={onChangeAlignment}
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, 1fr)',
                        gap: theme.space.m,
                    }}
                >
                    <RadioCard value="left" icon={{ name: 'AlignLeft' }}>
                        Left
                    </RadioCard>
                    <RadioCard value="center" icon={{ name: 'AlignCenter' }}>
                        Center
                    </RadioCard>
                </RadioCardGroup>
            </Field>
            <Field label="Label placement">
                <RadioCardGroup
                    value={labelPlacement}
                    onValueChange={onChangeLabelPlacement}
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, 1fr)',
                        gap: theme.space.m,
                    }}
                >
                    <RadioCard value="hide" icon={{ name: 'EyeOff' }}>
                        Hide
                    </RadioCard>
                    <RadioCard value="top" icon={{ name: 'ArrowUpFromLine' }}>
                        Top
                    </RadioCard>
                    <RadioCard value="left" icon={{ name: 'ArrowLeftFromLine' }}>
                        Left
                    </RadioCard>
                </RadioCardGroup>
            </Field>
            <Field
                htmlFor="showFieldIcon"
                label="Show field icon"
                rightSlotContent={
                    <Toggle
                        id="showFieldIcon"
                        checked={showFieldIcon}
                        onCheckedChange={onChangeShowFieldIcon}
                    />
                }
            />
        </Box>
    )
}

type RelatedRecordWidgetActionControlsProps = {
    object?: ObjectDto
}

const RelatedRecordWidgetActionControls: WidgetAdminControlsComponent<
    RelatedRecordWidgetType,
    RelatedRecordWidgetActionControlsProps
> = ({ widget, onChange, object }) => {
    const actionButtons = widget.attrs.actionButtons ?? []
    const onChangeActionButtons = useCallback(
        ({ pageButtons }: { pageButtons: ActionButton[] }) => {
            onChange((attrs) => {
                attrs.set('actionButtons', toYType(pageButtons))
            })
        },
        [onChange]
    )

    if (!object) return null

    return (
        <Box px="l" flex column height="full" gap="3xl">
            <ActionsContextProvider object={object}>
                <ActionButtonsSelector
                    object={object}
                    selectedButtons={actionButtons}
                    setConfig={onChangeActionButtons}
                    showHeader={true}
                    canDeleteActions={true}
                    canEditActions={true}
                    canSearchActions={true}
                    canReorderActions={true}
                    animate={false}
                    height="auto"
                    maxHeight="none"
                    customHeader={({ onCreateActionClick }) => (
                        <Box flex center justifyContent="space-between" mb="m">
                            <Body size="m" weight="medium">
                                Select actions
                            </Body>
                            <Tooltip
                                asChild
                                zIndex={1500}
                                side="bottom"
                                align="end"
                                content="Create new action"
                            >
                                <Button
                                    variant="secondary"
                                    size="xs"
                                    onClick={onCreateActionClick}
                                    startIcon={{ name: 'Plus' }}
                                    aria-label="Create new action"
                                />
                            </Tooltip>
                        </Box>
                    )}
                />
            </ActionsContextProvider>
        </Box>
    )
}

type FieldDetailsControlProps = {
    fieldSid: string
}

const FieldDetailsControl: WidgetAdminControlsComponent<
    RelatedRecordWidgetType,
    FieldDetailsControlProps
> = ({ widget, onChange, fieldSid }) => {
    const { targetObjectFields } = useRelatedRecordWidgetTarget(widget)

    const field = targetObjectFields.find((f) => f._sid === fieldSid)
    if (!field) return <div />

    const existingAttribute = widget.attrs.fields.find((f) => f.sid === fieldSid)
    const widgetAttributeField: FieldCollectionAttribute = {
        sid: fieldSid,
        apiName: field?.api_name ?? '',
        conditions: [],
        ...existingAttribute,
        field,
    }

    const handleFieldChange = (patch: Partial<FieldCollectionAttribute>) => {
        onChange((attrs) => {
            const fields = attrs.get('fields')

            const field = yUtils.arrayFind<typeof attrs>(fields, (f) => f.get('sid') === fieldSid)
            if (!!field) {
                for (const [key, value] of Object.entries(patch)) {
                    // Ignore the field property.
                    if (key === 'field') continue
                    field.set(key, toYType(value))
                }
                return
            }

            // Ignore the field property.
            const { field: _, ...newField }: FieldCollectionAttribute = {
                ...widgetAttributeField,
                ...patch,
            }
            fields.push([toYType(newField)])
        })
    }

    return (
        <FieldCollectionDetailsControl field={widgetAttributeField} onChange={handleFieldChange} />
    )
}
